import './Navbar.scss';
import './Navbar.m.scss';
import { Component } from '@components/Component/Component.js';
import DIContainer, { getInstance } from '@components/DependencyInjectionContainer.js';
import { ButtonsMixin } from '@components/Mixin/ButtonsMixin/ButtonsMixin.js';
import { convertLegacyQ360Url, getCookie, isMobile, isUrl, oAuthNative, oAuthPopup, setCookie, unsetCookie } from '@components/Utility/index.js';
import { ContextMenuMixin } from '@components/Mixin/ContextMenuMixin/ContextMenuMixin.js';
import { ENV, TOKEN } from '@components/Environment.js';
import { switchMap } from 'rxjs/operators';
import { zoomHandler } from '@components/Utility/index.js';
import { map } from "rxjs";
import { eventContextGet } from '@components/EventConfigManager/EventConfigManagerUtil.js';
// import { startCreditCardScanner, CreditCardScannerScreenConfiguration } from 'capacitor-plugin-scanbot-sdk/ui_v2';
// import {ScanbotSDK} from 'capacitor-plugin-scanbot-sdk';
/**
 * Class `Navbar` extends from the `Component` class.
 * It represents a navigation bar with interactive navigation options.
 */
export class Navbar extends ButtonsMixin(ContextMenuMixin(Component)) {
    constructor() {
        super(...arguments);
        this.componentName = 'Navbar';
        this.showMenu = false;
        /** Boolean to include quick search controls on the action bar. */
        this._includeQuickSearch = false;
        this._includeLogo = true;
        this._includeTileButton = false;
        this._includeScreenMenuButton = false;
        this._title = '';
        this.hotkeys = {
            'enter': () => {
                if (this.quickSearchInput && this.quickSearchInput.propertyGet('focused')) {
                    this.quickSearch();
                }
            }
        };
    }
    get includeQuickSearch() { return this._includeQuickSearch; }
    set includeQuickSearch(value) {
        this._propertySet('includeQuickSearch', value);
        this.renderQuickSearch();
    }
    get includeLogo() { return this._includeLogo; }
    set includeLogo(value) {
        this._propertySet('includeLogo', value);
        this.renderLogo();
    }
    get includeTileButton() {
        return this._includeTileButton;
    }
    set includeTileButton(value) {
        this._propertySet('includeTileButton', value);
        this.refreshButton('rightmenu_tile_btn');
    }
    get includeScreenMenuButton() {
        return this._includeScreenMenuButton;
    }
    set includeScreenMenuButton(value) {
        this._propertySet('includeScreenMenuButton', value);
        this.refreshButton('rightmenu_screenmenu_btn');
    }
    get title() {
        return this._title;
    }
    set title(value) {
        this._propertySet('title', value ?? '');
        this.updateTitleUI();
    }
    renderLogo() {
        if (!this.drawn)
            return;
        const logoContainer = this.rootElement?.querySelector('.navbar-logo');
        logoContainer?.classList.toggle('d-none', !this._includeLogo);
    }
    updateTitleUI() {
        const container = this.rootElement?.querySelector('.navbar-form-title');
        const el = this.rootElement?.querySelector('.title-bar h3');
        const isMobileApp = ENV('appName') === 'q360MobileApp';
        container?.classList.toggle('d-none', !isMobileApp);
        this.rightMenu?.classList.toggle('mobile', isMobileApp);
        if (el && isMobileApp) {
            el.textContent = this._title;
        }
    }
    /**
     * Access to the containing ScreenManager.
     * @constructor
     */
    get ScreenManager() {
        if (!this.dependencies.ScreenManager) {
            // we didn't receive a request dependency injection, so just use the global singleton
            return DIContainer.getInstance('ScreenManager');
            // this.dependencies.ScreenManager = // don't set the dependency in this case because screen manager can be swapped out.
        }
        return this.dependencies.ScreenManager;
    }
    /**
     * This method is called to draw the component on the screen.
     */
    draw() {
        super.draw();
        // don't run when not logged in
        if (this.showMenu) {
            this.leftMenu = this.rootElement.querySelector('.navbar-left');
            this.rightMenu = this.rootElement.querySelector('.navbar-right');
            this.setupNav();
            // this.setupNavEvents();
        }
        if (this.includeQuickSearch) {
            this.renderQuickSearch();
        }
        if (!this.includeLogo) {
            this.renderLogo();
        }
        this.updateTitleUI();
    }
    quickSearch() {
        const val = this.quickSearchInput?.value;
        const app = getInstance('Application');
        if (!val) {
            return;
        }
        if (isUrl(val)) {
            const url = convertLegacyQ360Url(val);
            this.ScreenManager.open(url);
            this.quickSearchInput.value = '';
        }
        else {
            DIContainer.getInstance('Request').GET({
                endpoint: 'quicksearch',
                action: 'list',
                initiator: this.quickSearchInput,
                requestData: {
                    autocomplete: val
                }
            }).pipe(map(resp => resp.payload.result ?? [])).subscribe((results) => {
                if (!results.length) {
                    app.alert({ captionKey: 'system_noresults' });
                    return;
                }
                if (results.length > 1) {
                    const panelSet = app.componentGet('panelset');
                    const gsPanel = panelSet?.componentGet('globalsearch_panel');
                    const gs = gsPanel?.componentGet('globalsearch');
                    // the panel logic is coupled to the Q360 app's panelSet. Otherwise just open the QuickSearch form
                    if (!panelSet || !gsPanel || !gs) {
                        this.ScreenManager.open('QuickSearch', {
                            requestParams: { autocomplete: val }
                        });
                    }
                    else {
                        gs.record?.set('nickname', val);
                        gs.searchResultsGrid?.clearOptionData();
                        gs.searchResultsGrid?.programmaticResponse(results);
                        panelSet.togglePanel(gsPanel.id, true);
                    }
                }
                else {
                    // one result - open it
                    this.ScreenManager.open(results[0].fullurl);
                }
                this.quickSearchInput.value = '';
            });
        }
    }
    focusQuickSearch() {
        this.quickSearchInput?.focus();
    }
    /**
     * Creates and initializes button containers for left and right menus in the navbar.
     * @protected
     *
     * @returns {void} The method does not return anything.
     */
    buttonContainerCreate() {
        const leftMenu = this.rootElement.querySelector('.navbar-left>.navbar-button-container');
        const rightMenu = this.rootElement.querySelector('.navbar-right>.navbar-button-container');
        if (leftMenu) {
            this.buttonContainers.set('left-menu', leftMenu);
        }
        if (rightMenu) {
            this.buttonContainers.set('right-menu', rightMenu);
        }
    }
    renderQuickSearch() {
        if (!this.drawn) {
            return;
        }
        const shouldShow = this.includeQuickSearch && !isMobile();
        // Only create the input when it should be visible (and the container exists in this template).
        if (!this.quickSearchInput && shouldShow) {
            const rootElement = this.rootElement.querySelector('[data-componentid="quicksearch"]');
            if (!rootElement) {
                return;
            }
            this.quickSearchInput = DIContainer.new('Input', 'quicksearch', rootElement);
            this.quickSearchInput.showLabel = false;
            this.quickSearchInput.captionKey = 'system_quicksearch';
            this.quickSearchInput.enabledRecordStates = ['none', 'edit'];
            this.quickSearchInput.draw();
            this.quickSearchInput.loadingThrobberRoot = rootElement;
        }
        if (this.quickSearchInput) {
            const searchContainer = this.rootElement.querySelector('.navbar-search-container');
            searchContainer?.classList.toggle('show', shouldShow);
            this.quickSearchInput.hidden = !shouldShow;
        }
    }
    /**
     * Sets up the NavBar by getting the JWT payload and adding the menu buttons.
     *
     * @returns {void}
     */
    setupNav() {
        this.addMenuButtons();
    }
    /**
     * Adds menu buttons to the defined containers.
     *
     * @returns {void}
     */
    addMenuButtons() {
        const right = 'right-menu';
        //const left = 'left-menu';
        /*if (this.includeQuickSearch) {
            this.buttonAdd({
                icon: 'search'
                , id: 'rightmenu_search_btn'
                , containerId: 'right-menu'
                , classList: ['nav-button']
                , index: 0
                // ,invertColors: true
                , onClick: (e) => {
                    this.toggleSearch();
                }
            })
        }*/
        this.buttonAdd({
            icon: 'more_vert',
            id: 'rightmenu_screenmenu_btn',
            containerId: right,
            classList: ['nav-button'],
            index: 1,
            includeProperty: 'includeScreenMenuButton',
            onClick: (e) => {
                this.ScreenManager.activeScreenGet()?.displayScreenMenu(e);
            }
        });
        this.buttonAdd({
            icon: 'apps',
            id: 'rightmenu_tile_btn',
            containerId: right,
            classList: ['nav-button'],
            index: 1,
            includeProperty: 'includeTileButton',
            captionKey: 'system_navbar_tiles',
            onClick: () => {
                const screenGroup = this.ScreenManager.activeScreenGroupGet();
                screenGroup.tileWindows();
            }
        });
        if (ENV('devMode')) {
            this.buttonAdd({
                icon: 'build_circle',
                id: 'rightmenu_dev_btn',
                containerId: right,
                classList: ['nav-button'],
                index: 2
                // ,invertColors: true
                ,
                onClick: (e) => {
                    this.menuClickHandler('dev', e);
                }
            });
        }
        if (ENV('userType').toUpperCase() !== 'EXTERNAL' && ENV('appName') !== 'q360MobileApp') {
            this.buttonAdd({
                icon: 'help',
                id: 'rightmenu_help_btn',
                containerId: right,
                classList: ['nav-button'],
                index: 2,
                captionKey: 'system_navbar_helpmenu'
                // ,invertColors: true
                ,
                onClick: (e) => {
                    this.menuClickHandler('help', e);
                }
            });
        }
        this.buttonAdd({
            caption: isMobile() ? '' : (ENV('userName') || ''),
            icon: 'account_circle',
            id: 'rightmenu_user_btn',
            containerId: right,
            classList: ['nav-button'],
            index: 3,
            captionKey: 'system_navbar_usermenu'
            // ,invertColors: true
            ,
            onClick: (e) => {
                this.menuClickHandler('user', e);
            }
        });
    }
    /**
     * Toggles the visibility of a menu based on the menu name and target.
     *
     * @param {string} menuName - The name of the menu to toggle. Possible values are "help" or "user".
     * @param {Event} event - The event that triggered the menu toggle.
     */
    toggleMenu(menuName, event) {
        let button = event.target;
        while (button.parentNode && !button.classList.contains('nav-button')) {
            button = button.parentNode;
        }
        //let buttonContainer = button.parentNode as HTMLSpanElement;
        // retrieve the menu
        let menu;
        switch (menuName) {
            case 'help':
                menu = this.rightMenu?.querySelector('.help-menu-dropdown');
                break;
            case 'user':
                menu = this.rightMenu?.querySelector('.user-menu-dropdown');
                break;
        }
        if (menu && button) {
            // position and show the menu
            const btnRect = button?.getBoundingClientRect();
            const cntRect = button?.getBoundingClientRect();
            menu.classList.toggle('show');
            menu.style.top = (cntRect.bottom) + 'px';
            menu.style.left = (window.scrollX + btnRect.right - menu.offsetWidth) + 'px';
        }
    }
    // /**
    //  * Registers click event on NavBar elements and handles click events.
    //  *
    //  * @returns {void}
    //  */
    // protected setupNavEvents() {
    // 	const toolbarUl = this.rootElement.querySelector('nav');
    //
    // 	if (toolbarUl) {
    // 		this.registerEvent('click', toolbarUl, (e) => {
    // 			const target = e.target as HTMLAnchorElement;
    // 			if (target.tagName.toLowerCase() === 'a') {
    // 				e.preventDefault();
    //
    // 				if (target.getAttribute('onClick')) {
    // 					// allow the onClick code to run instead.
    // 					return;
    // 				}
    //
    // 				if (target.getAttribute('href') == '#') {
    // 					console.error('No href');
    // 					return;
    // 				}
    //
    // 				DIContainer.getInstance<ScreenManager>('ScreenManager').open(target.href, {
    // 					title: target.textContent || ''
    // 				});
    // 			}
    // 		});
    // 	}
    // }
    getUserMenuItems() {
        const extUser = ENV('userType').toUpperCase() === 'EXTERNAL';
        const menuItems = [
            {
                caption: 'Themes',
                children: this.getThemeMenuItems()
            }
        ];
        if (!extUser && ENV('appName') !== 'q360MobileApp') {
            menuItems.push({
                caption: 'Sizing',
                children: this.getSizingMenuItems()
            });
            menuItems.push({
                caption: 'Screen Mode',
                children: this.getScreenModeMenuItems()
            });
        }
        if (ENV('appName') === 'q360MobileApp') {
            menuItems.push({
                caption: 'Refresh Cache',
                onClick: () => {
                    const app = getInstance('Application');
                    app.clearCache();
                    app.preCacheForms({ refreshCache: true });
                }
            });
        }
        else {
            menuItems.push({
                caption: 'Clear Cache',
                onClick: () => {
                    getInstance('Application').clearCache();
                    DIContainer.getInstance('ToastManager').toast({
                        message: this.CaptionManager.getCaption('system_cachecleared'),
                        subject: this.CaptionManager.getCaption('system_success'),
                        style: 'success'
                    });
                }
            });
        }
        if (ENV('appName') !== 'q360MobileApp') {
            menuItems.push({
                caption: isMobile() ? 'Desktop Mode' : 'Mobile Mode',
                onClick: () => {
                    getInstance('Application').updateMobileMode(!isMobile());
                }
            });
        }
        if (ENV('appName') === 'q360') {
            if (!extUser) {
                menuItems.push({
                    caption: 'User Preferences',
                    onClick: () => {
                        DIContainer.resolveLazy('UserPreferenceModal')
                            .subscribe((modal) => {
                            modal.open().subscribe();
                        });
                    }
                });
            }
            menuItems.push({
                caption: 'Change Password',
                onClick: () => {
                    DIContainer.resolveLazy('ChangePasswordModal')
                        .subscribe((modal) => {
                        modal.open().subscribe();
                    });
                }
            });
        }
        if (ENV('appName') === 'q360' || ENV('appName') === 'q360MobileApp') {
            if (TOKEN('{{USER_EMAIL}}') && ENV('userType') !== 'EXTERNAL' && ENV('webmailAuthType') !== 'application') {
                menuItems.push({
                    caption: 'Authorize Email',
                    onClick: () => {
                        const ctx = eventContextGet({ initiator: this });
                        const params = {
                            email: TOKEN('{{USER_EMAIL}}'),
                            mode: 'authorize_email'
                        };
                        (ENV('isNative')
                            ? oAuthNative(params, ctx)
                            : oAuthPopup(params, ctx)).subscribe({
                            next: (result) => {
                                ctx.alert({ captionKey: 'system_success', modalStyle: 'success' });
                            },
                            error: (err) => {
                                console.log('ERROR Authorizing', err);
                            }
                        });
                    }
                });
            }
        }
        if (ENV('appName') === 'formbuilder') {
            menuItems.push({
                caption: 'Script All Forms',
                onClick: () => {
                    getInstance('Application').confirm({
                        caption: 'IMPORTANT: Make sure you have fully up to date SVN, and then run Local-Install to ensure you dont blow away changes in form definitions before proceeding.',
                        modalStyle: 'warning'
                    })
                        .pipe(switchMap(() => {
                        return DIContainer.getInstance('Request').POST({
                            endpoint: 'form',
                            action: 'scriptAll',
                            requestData: {
                                _formname: 'q360form',
                                _q360formno: ''
                            }
                        });
                    })).subscribe();
                }
            });
            menuItems.push({
                caption: 'Script All Grids',
                onClick: () => {
                    getInstance('Application').confirm({
                        caption: 'IMPORTANT: Make sure you have fully up to date SVN, and then run Local-Install to ensure you dont blow away changes in form definitions before proceeding.',
                        modalStyle: 'warning'
                    })
                        .pipe(switchMap(() => {
                        return DIContainer.getInstance('Request').POST({
                            endpoint: 'grid',
                            action: 'exportGridDefs'
                        });
                    })).subscribe();
                }
            });
            menuItems.push({
                caption: 'Conditions Editor',
                onClick: () => {
                    DIContainer.getInstance('ScreenManager').open('conditionseditor');
                }
            });
            menuItems.push({
                caption: 'Form Builder',
                onClick: () => {
                    DIContainer.getInstance('ScreenManager').open('');
                }
            });
        }
        if (['mobiletech'].indexOf(ENV('appName')) > -1 && 'serviceWorker' in navigator) {
            menuItems.push({
                caption: 'Refresh App',
                onClick: () => {
                    navigator.serviceWorker.getRegistration().then(async (registration) => {
                        if (registration) {
                            await registration.update();
                            alert('update done');
                            // Unregister the current service worker
                            registration.unregister().then(success => {
                                console.log('Service Worker unregistered:', success);
                                // Optionally clear caches if needed
                                caches.keys().then(cacheNames => {
                                    return Promise.all(cacheNames.map(cacheName => {
                                        console.log('Deleting cache:', cacheName);
                                        return caches.delete(cacheName);
                                    }));
                                }).then(() => {
                                    console.log('All caches cleared.');
                                    // Force a reload to trigger re-installation of the service worker
                                    window.location.reload();
                                });
                            });
                        }
                        else {
                            // If there is no service worker registered, just reload the page.
                            window.location.reload();
                        }
                    });
                }
            });
        }
        menuItems.push({
            caption: 'Logout',
            onClick: () => getInstance('Application').logout()
        });
        if (ENV('appName') === 'q360MobileApp') {
            menuItems.push({
                caption: 'Reset App',
                onClick: () => {
                    getInstance('Application').reset();
                }
            });
        }
        /*
        Proof of concept credit card scanning

        if (ENV('isNative')) {
            menuItems.push({
                caption: 'Scan Card POC',
                onClick: async () => {


                    // const license = await ScanbotSDK.getLicenseInfo();
                    // if (!license.isLicenseValid) {
                    // 	console.warn('Scanbot license is not valid (or trial expired).');
                    // 	return null;
                    // }

                    const config = new CreditCardScannerScreenConfiguration();
                    const result = await startCreditCardScanner(config);

                    if (result.status === 'OK') {
                        // DO NOT CONSOLE LOG THE RESULT. This was a quick Proof of concept only and it did work successfully.

                        // You’ll get back the scanned document result object.
                        // Next step is typically: show pages, export PDF, upload, etc.
                        // alert(JSON.stringify(result));
                        // console.log(JSON.stringify(result));
                        // result.creditCard
                        // console.log('Card Number:', result.card.number);
                        // console.log('Expiry:', result.card.expiryDate);
                        // alert('Cardholder:' +  result.data.name);

                        return result;
                    }

                    // User canceled / error states etc.
                    return result;






                }
            });
        }*/
        return menuItems;
    }
    getThemeMenuItems() {
        const currentTheme = ENV('theme') || '';
        return [
            {
                caption: 'System',
                icon: currentTheme === '' ? 'check' : '',
                onClick: () => getInstance('Application').updateTheme('')
            },
            {
                caption: 'Light',
                icon: currentTheme === 'light' ? 'check' : '',
                onClick: () => getInstance('Application').updateTheme('light')
            },
            {
                caption: 'Dark',
                icon: currentTheme === 'dark' ? 'check' : '',
                onClick: () => getInstance('Application').updateTheme('dark')
            },
            {
                caption: 'Dark-Alt',
                icon: currentTheme === 'dark-alt' ? 'check' : '',
                onClick: () => getInstance('Application').updateTheme('dark-alt')
            },
            {
                caption: 'Cream',
                icon: currentTheme === 'cream' ? 'check' : '',
                onClick: () => getInstance('Application').updateTheme('cream')
            },
            {
                caption: 'Mist',
                icon: currentTheme === 'mist' ? 'check' : '',
                onClick: () => getInstance('Application').updateTheme('mist')
            },
            {
                caption: 'Sand',
                icon: currentTheme === 'sand' ? 'check' : '',
                onClick: () => getInstance('Application').updateTheme('sand')
            },
            {
                caption: 'Dark + Blue',
                icon: currentTheme === 'dark-blue' ? 'check' : '',
                onClick: () => getInstance('Application').updateTheme('dark-blue')
            },
            {
                caption: 'Dark + Green',
                icon: currentTheme === 'dark-green' ? 'check' : '',
                onClick: () => getInstance('Application').updateTheme('dark-green')
            },
            {
                caption: 'Dark + Red',
                icon: currentTheme === 'dark-red' ? 'check' : '',
                onClick: () => getInstance('Application').updateTheme('dark-red')
            },
            {
                caption: 'Mono-Q',
                icon: currentTheme === 'mono-q' ? 'check' : '',
                onClick: () => getInstance('Application').updateTheme('mono-q')
            },
            {
                caption: 'Carbon',
                icon: currentTheme === 'carbon' ? 'check' : '',
                onClick: () => getInstance('Application').updateTheme('carbon')
            },
            {
                caption: 'Graphite',
                icon: currentTheme === 'graphite' ? 'check' : '',
                onClick: () => getInstance('Application').updateTheme('graphite')
            },
            {
                caption: 'Charcoal',
                icon: currentTheme === 'charcoal' ? 'check' : '',
                onClick: () => getInstance('Application').updateTheme('charcoal')
            },
            {
                caption: 'Slate',
                icon: currentTheme === 'slate' ? 'check' : '',
                onClick: () => getInstance('Application').updateTheme('slate')
            },
            {
                caption: 'Navy',
                icon: currentTheme === 'navy' ? 'check' : '',
                onClick: () => getInstance('Application').updateTheme('navy')
            },
            {
                caption: 'Chocolate',
                icon: currentTheme === 'chocolate' ? 'check' : '',
                onClick: () => getInstance('Application').updateTheme('chocolate')
            },
            {
                caption: 'Pastel Blue',
                icon: currentTheme === 'pastel-blue' ? 'check' : '',
                onClick: () => getInstance('Application').updateTheme('pastel-blue')
            },
            {
                caption: 'Pastel Lavender',
                icon: currentTheme === 'pastel-lavender' ? 'check' : '',
                onClick: () => getInstance('Application').updateTheme('pastel-lavender')
            },
            {
                caption: 'Pastel Mint',
                icon: currentTheme === 'pastel-mint' ? 'check' : '',
                onClick: () => getInstance('Application').updateTheme('pastel-mint')
            },
            {
                caption: 'Vibrant Teal',
                icon: currentTheme === 'vibrant-teal' ? 'check' : '',
                onClick: () => getInstance('Application').updateTheme('vibrant-teal')
            }
        ];
    }
    getSizingMenuItems() {
        const sizing = ENV('sizing');
        return [
            {
                caption: 'Normal',
                icon: sizing === '' ? 'check' : '',
                onClick: () => getInstance('Application').updateSizing('')
            },
            {
                caption: 'Compact',
                icon: sizing === 'compact' ? 'check' : '',
                onClick: () => getInstance('Application').updateSizing('compact')
            }
        ];
    }
    getScreenModeMenuItems() {
        const screenMode = ENV('screenMode');
        const modes = [
            {
                caption: 'Tab Mode',
                icon: screenMode === 'tab' ? 'check' : '',
                onClick: () => {
                    getInstance('Application').updateScreenMode('tab');
                }
            },
            {
                caption: 'Window Mode',
                disabled: isMobile(),
                disabledMessage: 'Windowed mode not available on mobile.',
                icon: screenMode === 'window' ? 'check' : '',
                onClick: () => {
                    getInstance('Application').updateScreenMode('window');
                }
            }
        ];
        if (isMobile() && this.ScreenManager.screensList().length < 2) {
            modes.push({
                caption: 'Single Page Mode',
                icon: screenMode === 'single' ? 'check' : '',
                onClick: () => {
                    getInstance('Application').updateScreenMode('single');
                }
            });
        }
        return modes;
    }
    /*protected getViewModeMenuItems():ContextMenuItemInterface[] {
        return [
            {
                caption: 'Full View',
                onClick: () => getInstance<Application>('Application').updateViewMode('full')
            },
            {
                caption: 'Tech View',
                onClick: () => getInstance<Application>('Application').updateViewMode('tech')
            }
        ]
    }*/
    getHelpMenuItems() {
        const items = [];
        items.push({
            caption: 'Send Email to Customer Care',
            onClick: () => {
                zoomHandler('/form/sendemail?emailtemplatetype=CustomerCare&browser={{browser}}&version={{version}}', {
                    target: '',
                    data: {
                        browser: navigator.userAgent,
                        version: ENV('clientVersion')
                    }
                });
            }
        });
        if (ENV('appName') === 'q360' && ENV('userType') !== 'EXTERNAL') {
            items.push({
                caption: 'Content',
                disabled: ENV('appName') !== 'q360',
                onClick: () => {
                    // Open synchronously to satisfy iOS Safari's user-gesture requirement, window.open doesn't work after waiting for async response.
                    const pendingWin = window.open('', '_blank'); // may be null if blocked
                    if (pendingWin) {
                        pendingWin.document.write('<p style="font:14px system-ui">Loading help…</p>');
                    }
                    DIContainer.getInstance('Request').POST({
                        endpoint: 'help',
                        action: 'linkGet',
                        showErrors: true,
                        requestData: {
                            appname: ENV('appName')
                        }
                    }).subscribe({
                        next: (response) => {
                            const url = response.payload.outvars?.url;
                            if (url && pendingWin) {
                                pendingWin.location.href = url; // works on iOS
                                return;
                            }
                            else {
                                if (pendingWin)
                                    pendingWin.close();
                            }
                            // error generating url
                            DIContainer.new('Alert').open({
                                caption: 'Error generating Help Site URL.'
                            });
                        },
                        error: () => {
                            if (pendingWin)
                                pendingWin.close();
                        }
                    });
                }
            });
        }
        items.push({
            caption: 'About',
            onClick: () => {
                const currentYear = new Date().getFullYear();
                let version = `Client Version: ${ENV('clientVersion')} \nDatabase Version: ${ENV('dbVersion')}`;
                const trademark = `\n\n© ${currentYear} Solutions360 Inc. All rights reserved.`;
                if (+ENV('hotfixVersion') > 0) {
                    version = `Client Version: ${ENV('clientVersion')}-${ENV('hotfixVersion')} \nDatabase Version: ${ENV('dbVersion')}`;
                }
                getInstance('Application').alert({
                    //caption: `Client Version: ${ENV('clientVersion')} \nDatabase Version: ${ENV('dbVersion')}\n\n© ${currentYear} Solutions360 Inc. All rights reserved.`
                    caption: version + trademark
                });
            }
        });
        return items;
    }
    getDevMenuItems() {
        const items = [];
        items.push({
            caption: 'Memory Monitor',
            icon: getInstance('MemoryMonitor').hidden ? '' : 'check',
            onClick: () => {
                if (getInstance('MemoryMonitor').hidden) {
                    getInstance('MemoryMonitor').start();
                }
                else {
                    getInstance('MemoryMonitor').stop();
                }
            }
        });
        items.push({
            caption: 'XDebug',
            icon: getCookie('XDEBUG_SESSION') ? 'check' : '',
            onClick: () => {
                const hasCookie = !!getCookie('XDEBUG_SESSION');
                getInstance('Application').saveUserPreference('xdebug', hasCookie ? 'false' : 'true', '%');
                if (hasCookie) {
                    unsetCookie('XDEBUG_SESSION');
                }
                else {
                    setCookie('XDEBUG_SESSION', 'PHPSTORM');
                }
            }
        });
        if (ENV('appName') === 'q360') {
            items.push({
                caption: 'Console Errors',
                icon: 'terminal',
                onClick: () => {
                    const app = DIContainer.getInstance('Application');
                    const panelSet = app.componentGet('panelset');
                    if (panelSet && panelSet.currentPanelRight?.id !== 'errors_panel') {
                        panelSet.togglePanel('errors_panel');
                    }
                }
            });
        }
        return items;
    }
    menuClickHandler(menu, event) {
        let button = event.target;
        while (button.parentNode && !button.classList.contains('nav-button')) {
            button = button.parentNode;
        }
        if (button) {
            //this.getContextMenuItems()
            const rect = button.getBoundingClientRect();
            const x = rect.right;
            const y = rect.bottom;
            let items = [];
            const contextInfo = {
                targetElement: button,
                x: x,
                y: y,
                event: event,
                menu: menu,
                anchorPosition: 'top-right',
                initiator: this
            };
            items = this.getContextMenuItems(contextInfo);
            if (items) {
                this.ContextMenu.showMenu(items, contextInfo, `${this.globalId}_${menu}`);
            }
        }
    }
    getContextMenuItems(contextInfo) {
        let items = [];
        switch (contextInfo.menu) {
            case 'user':
                items = this.getUserMenuItems();
                break;
            case 'help':
                items = this.getHelpMenuItems();
                break;
            case 'dev':
                items = this.getDevMenuItems();
                break;
        }
        return items;
    }
    /**
     * This method creates the HTML structure of the component.
     * @returns A string of HTML.
     */
    template() {
        const brandingTemplate = `<div class="navbar-container navbar-logo">
	<span class="branding-logo"></span>
</div>`;
        if (!this.showMenu) {
            /* For modules like Login or EAccept which will not have a menu, we will use this for consistent styling and our logo. */
            return `<nav class="navbar">${brandingTemplate}</nav>`;
        }
        return `<nav class="navbar">
			<div class="navbar-container navbar-left">
				<span class="navbar-button-container"></span>
			</div>
			${brandingTemplate}
			<div class="navbar-container navbar-form-title title-bar">
				<h3></h3>
			</div>
			<div class="navbar-container navbar-right">
				<span class="navbar-search-container ${this.includeQuickSearch && !isMobile() ? 'show' : ''}">
					<span data-componentid="quicksearch" class="search-input">
					${this.IconManager.getIconString('search', { 'class': 'search-icon' })}</span>
<!--					<input type="text" class="search-input" placeholder="Quick Search...">-->
				</span>
				<span class="navbar-menu navbar-button-container"></span>
<!--				<div class="dropdown-container help-menu-dropdown">-->
<!--					<a href="#">Contents</a>-->
<!--				</div>-->
<!--				<div class="dropdown-container user-menu-dropdown">-->
				
				</div>
			</div>
		</nav>`;
    }
}
Navbar.VERSION = '$Revision: 130768 $';
